<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, PropType, ref } from 'vue';
import { BaseNamespaceFormDataModel, UserModel } from './models';
import { BaseNamespaceFormFields } from './types';
import {
  TirPmInput,
  TirPmIconPicker,
  IconType,
  IconColorType,
  TirDivider,
  SelectedIconModel,
  Tabs,
  Tab,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
} from 'tir-components';
import { AdapterService } from './adapter';
import { EmployeeMultiselect } from './components';
import { Skeleton } from './components/skeleton';
import { PlusIcon } from 'tir-style-system/icons/outline';

const adapter = new AdapterService();

/**События */
const emit = defineEmits<{
  /**Событие обновления данных формы */
  (e: 'update:modelValue', value: BaseNamespaceFormDataModel): void;
}>();
/**Параметры */
const props = defineProps({
  /**Данные формы */
  modelValue: {
    type: Object as PropType<BaseNamespaceFormDataModel>,
    default: () => new BaseNamespaceFormDataModel(),
  },
});

/**Табы */
const tabs = ['Администраторы', 'Команда'];

/**Текущий там */
const currentTab = ref<string>(tabs[0]);
/**Список иконок */
const icons = ref<Array<IconType>>();
/**Список цветов */
const colors = ref<Array<IconColorType>>();
/**Список сотрудников */
const employees = ref<Array<UserModel>>();
/**Состояние загрузки */
const isLoading = ref(false);

/**Название рабочей области */
const name = computed({
  get() {
    return props.modelValue.Name;
  },
  set(value) {
    emit(
      'update:modelValue',
      new BaseNamespaceFormDataModel({
        ...props.modelValue,
        Name: value,
      }),
    );
  },
});
/**Выбранная иконка */
const selectedIcon = computed({
  get() {
    return new SelectedIconModel({
      IconId: props.modelValue.IconId,
      ColorId: props.modelValue.IconColorId,
    });
  },
  set(value) {
    emit(
      'update:modelValue',
      new BaseNamespaceFormDataModel({
        ...props.modelValue,
        IconId: value.IconId,
        IconColorId: value.ColorId,
        IconUrl: icons.value?.find((icon) => icon.IconId === value.IconId)?.IconUrl,
        IconColor: colors.value?.find((color) => color.ColorId === value.ColorId)?.Color,
      }),
    );
  },
});
/**Выбранные идентификаторы админов */
const selectedAdminIds = computed({
  get() {
    return props.modelValue.AdminIds;
  },
  set(value) {
    console.log(value);

    emit(
      'update:modelValue',
      new BaseNamespaceFormDataModel({
        ...props.modelValue,
        AdminIds: value,
      }),
    );
  },
});
/**Выбранные идентификаторы команды */
const selectedTeamIds = computed({
  get() {
    return props.modelValue.TeamIds;
  },
  set(value) {
    emit(
      'update:modelValue',
      new BaseNamespaceFormDataModel({
        ...props.modelValue,
        TeamIds: value,
      }),
    );
  },
});

/**Изменение таба */
const changeTab = (tab: string) => {
  currentTab.value = tab;
};
/**Получить иконки */
const getIcons = async () => {
  const resp = await adapter.getIcons();
  if (resp.IsSuccess) icons.value = resp.Value;
};
/**Получить цвета иконок */
const getColors = async () => {
  const resp = await adapter.getColors();
  if (resp.IsSuccess) colors.value = resp.Value;
};
/**Получить список сотрудников */
const getEmployees = async () => {
  const resp = await adapter.getEmployees();
  if (resp.IsSuccess) employees.value = resp.Value;
};
/**Инициализация данных */
const initData = async () => {
  const promises = new Array<Promise<void>>();
  // Получение данных для пикера иконок
  promises.push(getIcons(), getColors());
  promises.push(getEmployees());

  if (promises.length) {
    isLoading.value = true;
    await Promise.all(promises);
    isLoading.value = false;
  }
};

/**До рендера компонента */
onBeforeMount(async () => {
  await initData();
});
</script>

<template>
  <Skeleton v-if="isLoading" />
  <div v-else class="base-namespace-form">
    <div class="base-namespace-form__group-row">
      <TirPmInput
        v-model="name"
        class="base-namespace-form__namespace-name"
        label="Название рабочей области"
        :is-with-hint="false"
      />
      <TirPmIconPicker v-model="selectedIcon" :icons="icons" :colors="colors" />
    </div>
    <div class="base-namespace-form__employees-select-group">
      <Tabs v-model="currentTab" @update:model-value="changeTab" class="base-namespace-form__tabs">
        <Tab :val="tabs[0]" :label="tabs[0]" :currentTab="currentTab">
          <div class="base-namespace-form__employees-select-count">
            {{ selectedAdminIds.length }}
          </div>
        </Tab>
        <Tab :val="tabs[1]" :label="tabs[1]" :currentTab="currentTab">
          <div class="base-namespace-form__employees-select-count">
            {{ selectedTeamIds.length }}
          </div>
        </Tab>
      </Tabs>
      <EmployeeMultiselect
        v-if="currentTab === tabs[0]"
        v-model="selectedAdminIds"
        :employees="employees"
        class="base-namespace-form__selected-components"
      >
        <template #targer>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Middle"
          >
            <template #leftSlot>
              <PlusIcon />
            </template>
            Добавить администратора
          </TirPmButton>
        </template>
      </EmployeeMultiselect>
      <EmployeeMultiselect
        v-else
        v-model="selectedTeamIds"
        :employees="employees"
        class="base-namespace-form__selected-components"
      >
        <template #targer>
          <TirPmButton
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Middle"
          >
            <template #leftSlot>
              <PlusIcon />
            </template>
            Добавить сотрудника
          </TirPmButton>
        </template>
      </EmployeeMultiselect>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.base-namespace-form {
  &__group-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__namespace-name {
    width: 100%;
  }
  &__divider {
    margin: 1.5rem 0;
  }
  &__employees-select-group {
    margin-top: 1.5rem;
  }
  &__tabs {
    width: max-content;
  }
  &__selected-components {
    margin-top: 1rem;
    width: 100%;
  }
  &__employees-select-count {
    font-weight: 400;
    margin-left: 0.125rem;
    color: var(--text-tertiary, #93969b);
  }
}
</style>
